<template>
  <div class="-full-preview-thumbnail" ref="videoPosterContainer">
    <div
      :class="[
        '-thumbnail',
        { '-noVideoPoster': !videoPosterAvailable },
        { '-error': producedBlocked && !anyPosterAvailable },
        { '-snapshotAvailable': snapshotAvailable },
        { '-noPotentialThumbnails': !hasPotentialThumbnails },
      ]"
      @click="openThumbnailPicker"
    >
      <i v-if="!anyPosterAvailable" class="wticon wticon-picture-sun"></i>

      <i v-if="onlyVideoPosterAvailable" class="wticon wticon-zoom-in"></i>

      <span
        class="-missing-thumb"
        v-if="!anyPosterAvailable && producedBlocked"
        >{{ 'fullPreview.MISSING_THUMBNAIL' | culture }}</span
      >

      <div
        class="-canvas-image-animation -animated"
        v-show="isTakingSnapshot"
      ></div>

      <img
        class="-canvas-image"
        v-if="canvasScreenshot.image"
        :src="canvasScreenshot.image"
      />

      <div
        class="-thumbnail-asset"
        v-if="videoPosterAvailable"
        :style="{ width: videoPosterContainerWidth + 'px' }"
      >
        <img
          v-if="!canvasScreenshot.image"
          :src="videoPosterUrl"
          :style="videoPosterStyle"
        />
      </div>
    </div>

    <div class="-desc">
      <div class="-desc-capture">
        <wt-button
          type="hollow-secondary"
          :class="[
            '-capture-btn',
            {
              '-disabled': !captureEnabled,
            },
          ]"
          v-tooltip="{
            title: captureTooltipMessage,
            placement: 'top',
            delay: 25,
            wrapperClass: 'capture-tooltip',
          }"
          @click="clickCapture"
          @mousemove.native="$emit('update:isCaptureHover', true)"
          @mouseout.native="$emit('update:isCaptureHover', false)"
          :text="'fullPreview.THUMBNAIL_MAKE_SNAPSHOT' | culture"
          icon="wticon-capture"
        />
      </div>
      <div class="-desc-choose-replace">
        <span class="wt2-color-normal">{{
          'fullPreview.THUMBNAIL_OR' | culture
        }}</span>
        <span
          class="wt2-color-primary wt-cursor-pointer"
          v-tooltip="{
            title: chooseDisabledTooltipMessage,
            placement: 'top',
            delay: 25,
          }"
          @click="openThumbnailPicker"
          :class="{
            'wt2-color-disabled': !hasPotentialThumbnails,
            'wt-cursor-default': !hasPotentialThumbnails,
          }"
        >
          {{
            videoPosterAvailable
              ? 'fullPreview.THUMBNAIL_REPLACE'
              : 'fullPreview.THUMBNAIL_CHOOSE' | culture
          }}
        </span>
        <span class="-error wt2-color-pink-d" v-if="errorMessage"></span>
      </div>
    </div>
  </div>
</template>

<script>
import app from 'app';
import audioNotification from 'c/audioNotification';

export default {
  props: {
    story: { type: Object, required: true },
    isPlayable: { type: Boolean },
    videoPoster: { type: Object, required: true },
    hasGaps: { type: Boolean, required: true },
    captureSnapshot: { type: Function, required: true },
    canvasScreenshot: { type: Object, required: true },
    isTakingSnapshot: { type: Boolean, required: true },
    producedBlocked: { type: Boolean, required: true },
  },

  data() {
    return {
      thumbnailDimensions: {
        height: 68,
        width: 120,
      },
      errorMessage: null,
    };
  },

  computed: {
    anyPosterAvailable() {
      return this.videoPosterAvailable || this.canvasScreenshot.image;
    },

    onlyVideoPosterAvailable() {
      return this.videoPosterAvailable && !this.canvasScreenshot.image;
    },

    snapshotAvailable() {
      return this.canvasScreenshot.image;
    },

    hasPotentialThumbnails() {
      return this.story.getPotentialThumbnails().length > 0;
    },

    captureEnabled() {
      return this.isPlayable && !this.hasGaps;
    },

    videoPosterAvailable() {
      return this.videoPoster && this.videoPoster.metadata;
    },

    captureTooltipMessage() {
      if (this.hasGaps) {
        return app.culture.get('fullPreview.CAPTURE_GAPS_TOOLTIP');
      } else if (!this.isPlayable) {
        return app.culture.get('fullPreview.CAPTURE_RENDER_TOOLTIP');
      } else {
        return false;
      }
    },

    chooseDisabledTooltipMessage() {
      if (!this.hasPotentialThumbnails) {
        return app.culture.get('fullPreview.CHOOSE_DISABLE_TOOLTIP');
      } else {
        return false;
      }
    },

    videoPosterUrl() {
      if (!this.videoPosterAvailable) {
        return;
      }

      return app.namespace.Controller.AssetController.getVideoPosterUrl(
        this.videoPoster
      );
    },

    videoPosterContainerWidth() {
      const newWidth =
        this.thumbnailDimensions.height * this.story.get('_aspectRatio').ratio;
      return newWidth;
    },

    videoPosterStyle() {
      const agd = this.videoPoster.agd;
      if (!this.videoPosterAvailable || _.isEmpty(agd)) {
        return;
      }

      const targetImage = {
        width: this.videoPoster.metadata.width,
        height: this.videoPoster.metadata.height,
      };

      const poster = {
        x: agd.cropX,
        y: agd.cropY,
        x1: agd.cropX1,
        y1: agd.cropY1,
      };

      const selection = {
        width: targetImage.width * poster.x1 - targetImage.width * poster.x,
        height: targetImage.height * poster.y1 - targetImage.height * poster.y,
      };

      const shift = {
        x: this.videoPosterContainerWidth / selection.width,
        y: this.thumbnailDimensions.height / selection.height,
      };

      return {
        width: Math.round(shift.x * targetImage.width) + 'px',
        'max-width': Math.round(shift.x * targetImage.width) + 'px',
        height: Math.round(shift.y * targetImage.height) + 'px',
        marginLeft:
          '-' + Math.round(shift.x * (targetImage.width * poster.x)) + 'px',
        marginTop:
          '-' + Math.round(shift.y * (targetImage.height * poster.y)) + 'px',
      };
    },
  },

  methods: {
    clickCapture() {
      if (this.captureEnabled) {
        app.analytics('fullPreview:captureSnapshot');
        this.captureSnapshot();
        audioNotification.capture();
      }
    },

    openThumbnailPicker() {
      if (this.hasPotentialThumbnails) {
        this.$emit('pause-playing');
        app.trigger('thumbnailPicker:openFromPreview');
      }
    },
  },
};
</script>
